import React, { useState, useRef } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, getCurrentUserOrganization } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Download, Upload, Plus, Edit, Trash2 } from 'lucide-react';
import { useActiveMandate } from '@/hooks/use-active-mandate';

// Swiss KMU Chart of Accounts Structure (1-8)
const swissAccountStructure = {
  "1": { name: "Aktiven", description: "Assets - Vermögensgegenstände", color: "bg-blue-100 text-blue-800" },
  "2": { name: "Passiven", description: "Liabilities - Verbindlichkeiten & Eigenkapital", color: "bg-red-100 text-red-800" },
  "3": { name: "Betriebsertrag", description: "Operating Revenue - Umsatzerlöse", color: "bg-green-100 text-green-800" },
  "4": { name: "Materialaufwand", description: "Material Expenses - Wareneinkauf", color: "bg-orange-100 text-orange-800" },
  "5": { name: "Personalaufwand", description: "Personnel Expenses - Löhne & Sozialleistungen", color: "bg-purple-100 text-purple-800" },
  "6": { name: "Übriger Betriebsaufwand", description: "Other Operating Expenses - Raumkosten, Versicherungen", color: "bg-yellow-100 text-yellow-800" },
  "7": { name: "Finanzergebnis", description: "Financial Result - Zinsen & Finanzierungskosten", color: "bg-indigo-100 text-indigo-800" },
  "8": { name: "Abschreibungen/Reserven", description: "Depreciation & Provisions - Wertberichtigungen", color: "bg-gray-100 text-gray-800" }
};

const ChartOfAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [formData, setFormData] = useState({
    account_number: '',
    account_name: '',
    account_class: '',
    account_type: '',
    description: '',
    default_vat_rate: '',
    swiss_vat_applicable: false
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { mandate, loading: mandateLoading, error: mandateError } = useActiveMandate();
  const mandateId = mandate?.id;

  // Fetch real accounts from Supabase
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['chart-of-accounts', mandateId],
    queryFn: async () => {
      if (!mandateId) {
        throw new Error('Kein aktives Mandat gefunden');
      }

      const org = await getCurrentUserOrganization();
      if (!org) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('organization_id', org.organization?.id ?? org.company_id)
        .eq('mandate_id', mandateId)
        .order('account_number', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: Boolean(mandateId),
  });

  // Create account mutation
  const createAccountMutation = useMutation({
    mutationFn: async (accountData: any) => {
      if (!mandateId) {
        throw new Error('Kein aktives Mandat gefunden');
      }

      const org = await getCurrentUserOrganization();
      if (!org) throw new Error('No organization found');

      const { error } = await supabase
        .from('mandate_chart_of_accounts')
        .insert({
          ...accountData,
          organization_id: org.organization?.id ?? org.company_id,
          mandate_id: mandateId,
          is_active: true
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts', mandateId] });
      toast.success('Konto erfolgreich erstellt');
      setIsAddDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(`Fehler: ${error.message}`);
    }
  });

  // Update account mutation
  const updateAccountMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      if (!mandateId) {
        throw new Error('Kein aktives Mandat gefunden');
      }

      const { error } = await supabase
        .from('mandate_chart_of_accounts')
        .update(data)
        .eq('id', id)
        .eq('mandate_id', mandateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts', mandateId] });
      toast.success('Konto erfolgreich aktualisiert');
      setIsEditDialogOpen(false);
      setSelectedAccount(null);
    },
    onError: (error: any) => {
      toast.error(`Fehler: ${error.message}`);
    }
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!mandateId) {
        throw new Error('Kein aktives Mandat gefunden');
      }

      const { error } = await supabase
        .from('mandate_chart_of_accounts')
        .delete()
        .eq('id', id)
        .eq('mandate_id', mandateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts', mandateId] });
      toast.success('Konto erfolgreich gelöscht');
      setIsDeleteDialogOpen(false);
      setSelectedAccount(null);
    },
    onError: (error: any) => {
      toast.error(`Fehler: ${error.message}`);
    }
  });

  const resetForm = () => {
    setFormData({
      account_number: '',
      account_name: '',
      account_class: '',
      account_type: '',
      description: '',
      default_vat_rate: '',
      swiss_vat_applicable: false
    });
  };

  const handleAddAccount = () => {
    if (!formData.account_number || !formData.account_name || !formData.account_class) {
      toast.error('Bitte fülle alle Pflichtfelder aus');
      return;
    }

    const cls = parseInt(formData.account_class, 10);
    if (Number.isNaN(cls) || cls < 1 || cls > 8) {
      toast.error('Kontoklasse muss zwischen 1 und 8 liegen');
      return;
    }

    createAccountMutation.mutate({
      account_number: formData.account_number.trim(),
      account_name: formData.account_name,
      account_class: cls,
      account_type: formData.account_type || 'operating',
      default_vat_rate: formData.default_vat_rate ? parseFloat(formData.default_vat_rate) : null,
      swiss_vat_applicable: formData.swiss_vat_applicable
    });
  };

  const handleEditAccount = () => {
    if (!selectedAccount) return;

    updateAccountMutation.mutate({
      id: selectedAccount.id,
      account_name: formData.account_name,
      account_type: formData.account_type,
      default_vat_rate: formData.default_vat_rate ? parseFloat(formData.default_vat_rate) : null,
      swiss_vat_applicable: formData.swiss_vat_applicable
    });
  };

  const openEditDialog = (account: any) => {
    setSelectedAccount(account);
    setFormData({
      account_number: account.account_number || '',
      account_name: account.account_name || '',
      account_class: account.account_class?.toString() || '',
      account_type: account.account_type || '',
      description: '',
      default_vat_rate: account.default_vat_rate?.toString() || '',
      swiss_vat_applicable: account.swiss_vat_applicable || false
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (account: any) => {
    setSelectedAccount(account);
    setIsDeleteDialogOpen(true);
  };

  // Export to CSV
  const handleExport = () => {
    if (!accounts.length) {
      toast.error('Keine Konten zum Exportieren');
      return;
    }

    const csv = [
      ['Kontonummer', 'Kontoname', 'Klasse', 'Typ', 'MWST-Satz', 'MWST anwendbar', 'Aktiv'].join(','),
      ...accounts.map(acc => [
        acc.account_number,
        `"${acc.account_name}"`,
        acc.account_class,
        acc.account_type || '',
        acc.default_vat_rate || '',
        acc.swiss_vat_applicable ? 'Ja' : 'Nein',
        acc.is_active ? 'Ja' : 'Nein'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `kontenplan_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Kontenplan exportiert');
  };

  // Import from CSV
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').slice(2); // Skip title row and header
        const currentMandateId = mandateId;
        if (!currentMandateId) {
          throw new Error('Kein aktives Mandat gefunden');
        }

        const org = await getCurrentUserOrganization();
        if (!org) throw new Error('No organization found');

        // Fetch existing accounts to check for duplicates
        const { data: existingAccounts } = await supabase
          .from('mandate_chart_of_accounts')
          .select('account_number')
          .eq('organization_id', org.organization?.id ?? org.company_id)
          .eq('mandate_id', currentMandateId);

        const existingNumbers = new Set(existingAccounts?.map(a => a.account_number) || []);

        // Parse CSV with format: Nummer*,Name*,Gruppe*,Kontoart*,Systemkonto,Verknüpftes Konto,MWST-Typ
        const importedAccounts = lines
          .filter(line => line.trim())
          .map(line => {
            // Handle quoted fields properly
            const fields: string[] = [];
            let currentField = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                fields.push(currentField.trim());
                currentField = '';
              } else {
                currentField += char;
              }
            }
            fields.push(currentField.trim());

            const [number, name, gruppe, kontoart, systemkonto, verknuepft, mwstTyp] = fields;
            
            // Skip if no account number or if it's a group/header row
            if (!number || kontoart?.toLowerCase() === 'gruppe') {
              return null;
            }

            // Sanitize account number and determine valid Swiss class (1-8)
            const rawNumber = (number || '').trim();
            // Match first non-space digit at start; must be 1-8 per Swiss KMU classes
            const classMatch = rawNumber.match(/^\s*([1-8])/);
            if (!classMatch) {
              // Skip invalid account numbers (e.g., headers or classes outside 1-8)
              return null;
            }
            const accountClass = parseInt(classMatch[1], 10);

            // Determine if VAT applicable
            const hasVAT = mwstTyp?.toLowerCase().includes('tax') || 
                          verknuepft?.toLowerCase() === 'ja' ||
                          mwstTyp?.toLowerCase().includes('sales') ||
                          mwstTyp?.toLowerCase().includes('pre');

            return {
              account_number: rawNumber,
              account_name: (name || '').replace(/"/g, ''),
              account_class: accountClass,
              account_type: kontoart?.toLowerCase() === 'aktiv' ? 'asset' : 
                           kontoart?.toLowerCase() === 'passiv' ? 'liability' :
                           kontoart?.toLowerCase() === 'ertrag' ? 'revenue' :
                           kontoart?.toLowerCase() === 'aufwand' ? 'expense' : 'operating',
              swiss_vat_applicable: hasVAT,
              is_active: true,
              organization_id: org.organization?.id ?? org.company_id,
              mandate_id: currentMandateId
            };
          })
          .filter((acc): acc is NonNullable<typeof acc> => acc !== null)
          .filter(acc => !existingNumbers.has(acc.account_number)); // Only new accounts

        if (importedAccounts.length === 0) {
          toast.info('Keine neuen Konten zu importieren (alle bereits vorhanden)');
          return;
        }

        // Insert new accounts only
        const { error } = await supabase
          .from('mandate_chart_of_accounts')
          .insert(importedAccounts);

        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ['chart-of-accounts', mandateId] });
        toast.success(`${importedAccounts.length} neue Konten importiert`);
      } catch (error: any) {
        toast.error(`Import fehlgeschlagen: ${error.message}`);
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file, 'UTF-8');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (mandateError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="max-w-lg p-8 text-center">
          <p className="text-sm text-red-600">
            Mandatsdaten konnten nicht geladen werden: {mandateError}
          </p>
        </Card>
      </div>
    );
  }

  if (mandateLoading || (!mandateId && isLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="max-w-lg p-8 text-center">
          <p className="text-sm text-muted-foreground">Mandatsinformationen werden geladen…</p>
        </Card>
      </div>
    );
  }

  if (!mandateId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="max-w-lg p-8 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Kein aktives Mandat gefunden. Bitte schließe den Onboarding-Prozess ab, um einen Kontenplan zu verwalten.
          </p>
        </Card>
      </div>
    );
  }

  const displayAccounts = accounts.map(account => ({
    ...account,
    id: account.account_number,
    name: account.account_name,
    class: account.account_number?.charAt(0) || '1',
    category: swissAccountStructure[account.account_number?.charAt(0) || '1']?.name || 'Unknown',
    balance: account.balance ? `CHF ${account.balance.toLocaleString('de-CH')}` : 'CHF 0',
    lastUsed: account.updated_at ? new Date(account.updated_at).toLocaleDateString('de-CH') : 'Nie'
  }));

  const filteredAccounts = displayAccounts.filter(account => {
    const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.id.includes(searchTerm);
    const matchesClass = selectedClass === 'all' || account.class === selectedClass;
    return matchesSearch && matchesClass;
  });

  return (
    <AppLayout>
      <PageHeader title="Kontenplan (Chart of Accounts)" subtitle="Swiss KMU accounting structure (Classes 1-8)" />
      <main className="flex-1 p-page-padding lg:p-page-padding-lg overflow-y-auto">
        {/* Page Header with Actions */}
        <div className="flex justify-between items-center mb-header-mb gap-4">
          <div>
            <h1 className="text-2xl font-bold text-brand-primary mb-2">Kontenplan (Chart of Accounts)</h1>
            <p className="text-muted-foreground">Swiss KMU accounting structure (Classes 1-8)</p>
          </div>

          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
            />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button className="btn-swiss-primary" onClick={() => {
              resetForm();
              setIsAddDialogOpen(true);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Neues Konto
            </Button>
          </div>
        </div>

        {/* Add Account Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neues Konto hinzufügen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="account-number">Kontonummer *</Label>
                <Input
                  id="account-number"
                  placeholder="z.B. 1030"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="account-name">Kontoname *</Label>
                <Input
                  id="account-name"
                  placeholder="z.B. Bank UBS"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="account-class">Kontoklasse *</Label>
                <Select value={formData.account_class} onValueChange={(value) => setFormData({ ...formData, account_class: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wähle Kontoklasse" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(swissAccountStructure).map(([classNum, details]) => (
                      <SelectItem key={classNum} value={classNum}>
                        {classNum} - {details.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vat-rate">MWST-Satz (%)</Label>
                <Input
                  id="vat-rate"
                  type="number"
                  step="0.1"
                  placeholder="z.B. 8.1"
                  value={formData.default_vat_rate}
                  onChange={(e) => setFormData({ ...formData, default_vat_rate: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="vat-applicable"
                  checked={formData.swiss_vat_applicable}
                  onChange={(e) => setFormData({ ...formData, swiss_vat_applicable: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="vat-applicable" className="cursor-pointer">MWST anwendbar</Label>
              </div>
              <Button
                className="w-full btn-swiss-primary"
                onClick={handleAddAccount}
                disabled={createAccountMutation.isPending}
              >
                {createAccountMutation.isPending ? 'Erstelle...' : 'Konto erstellen'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Account Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Konto bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Kontonummer</Label>
                <Input value={formData.account_number} disabled />
              </div>
              <div>
                <Label htmlFor="edit-account-name">Kontoname</Label>
                <Input
                  id="edit-account-name"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-vat-rate">MWST-Satz (%)</Label>
                <Input
                  id="edit-vat-rate"
                  type="number"
                  step="0.1"
                  value={formData.default_vat_rate}
                  onChange={(e) => setFormData({ ...formData, default_vat_rate: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-vat-applicable"
                  checked={formData.swiss_vat_applicable}
                  onChange={(e) => setFormData({ ...formData, swiss_vat_applicable: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="edit-vat-applicable" className="cursor-pointer">MWST anwendbar</Label>
              </div>
              <Button
                className="w-full btn-swiss-primary"
                onClick={handleEditAccount}
                disabled={updateAccountMutation.isPending}
              >
                {updateAccountMutation.isPending ? 'Speichere...' : 'Änderungen speichern'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Konto löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie das Konto "{selectedAccount?.account_name}" wirklich löschen?
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => selectedAccount && deleteAccountMutation.mutate(selectedAccount.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Swiss Account Classes Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          {Object.entries(swissAccountStructure).map(([classNum, details]) => (
            <Card key={classNum} className="p-4">
              <div className="flex items-center justify-between mb-2">
              <Badge className={details.color}>
                Klasse {classNum}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {displayAccounts.filter(acc => acc.class === classNum).length} Konten
              </span>
            </div>
              <h3 className="font-semibold text-sm">{details.name}</h3>
              <p className="text-xs text-muted-foreground mt-1">{details.description}</p>
            </Card>
          ))}
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Suche nach Kontonummer oder Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter nach Klasse" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Klassen</SelectItem>
              {Object.entries(swissAccountStructure).map(([classNum, details]) => (
                <SelectItem key={classNum} value={classNum}>
                  Klasse {classNum} - {details.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Accounts Table */}
        <Card className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-semibold text-sm">Kontonummer</th>
                  <th className="text-left py-3 px-2 font-semibold text-sm">Kontoname</th>
                  <th className="text-left py-3 px-2 font-semibold text-sm">Klasse</th>
                  <th className="text-right py-3 px-2 font-semibold text-sm">Saldo</th>
                  <th className="text-center py-3 px-2 font-semibold text-sm">Letzte Nutzung</th>
                  <th className="text-center py-3 px-2 font-semibold text-sm">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-2">
                      <span className="font-mono text-sm">{account.id}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="font-medium">{account.name}</span>
                    </td>
                    <td className="py-3 px-2">
                      <Badge className={swissAccountStructure[account.class as keyof typeof swissAccountStructure].color}>
                        {account.class} - {account.category}
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-sm">
                      <span className={account.balance.includes('-') ? 'text-red-600' : 'text-green-600'}>
                        {account.balance}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center text-xs text-muted-foreground">
                      {account.lastUsed}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => openEditDialog(accounts.find(a => a.account_number === account.id))}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-muted-foreground hover:text-red-600"
                          onClick={() => openDeleteDialog(accounts.find(a => a.account_number === account.id))}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAccounts.length === 0 && (
            <div className="text-center py-8">
              <i className="fa-solid fa-search text-4xl text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Konten gefunden für "{searchTerm}"</p>
            </div>
          )}
        </Card>

        {/* Swiss Accounting Info Footer */}
        <Card className="p-6 mt-8 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-4">
            <i className="fa-solid fa-info-circle text-blue-600 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Swiss KMU Kontenplan Information</h3>
              <p className="text-sm text-blue-800 mb-2">
                Der Schweizer KMU-Kontenplan folgt der strukturierten Klasseneinteilung 1-8 gemäss OR (Obligationenrecht) und Swiss GAAP FER.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-blue-700">
                <div>
                  <strong>Klassen 1-2:</strong> Bilanzkonten (Aktiven & Passiven)
                </div>
                <div>
                  <strong>Klassen 3-8:</strong> Erfolgskonten (Erträge & Aufwendungen)
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <footer className="mt-8 pt-6 border-t border-border flex justify-between items-center">
          <p className="text-xs text-foreground">Copyright © 2024 Swiss Finance OS</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="hover:text-brand-primary cursor-pointer transition-colors">Swiss GAAP FER</span>
            <span className="hover:text-brand-primary cursor-pointer transition-colors">OR Compliance</span>
            <span className="hover:text-brand-primary cursor-pointer transition-colors">KMU Guidelines</span>
          </div>
        </footer>
      </main>
    </AppLayout>
  );
};

export default ChartOfAccounts;
